import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { communicationsAPI, customersAPI, leadsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail, MessageSquare, User, Clock, ArrowUpRight, ArrowDownLeft } from 'lucide-react';

export default function Communications() {
  const [loading, setLoading] = useState(true);
  const [communications, setCommunications] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    lead_id: '',
    contact_name: '',
    contact_type: 'call',
    direction: 'outbound',
    subject: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [commsRes, customersRes, leadsRes] = await Promise.all([
        communicationsAPI.getAll(),
        customersAPI.getAll(),
        leadsAPI.getAll()
      ]);
      
      setCommunications(commsRes.data);
      setCustomers(customersRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_name || !formData.notes) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await communicationsAPI.create({
        customer_id: formData.customer_id || null,
        lead_id: formData.lead_id || null,
        contact_name: formData.contact_name,
        contact_type: formData.contact_type,
        direction: formData.direction,
        subject: formData.subject || null,
        notes: formData.notes
      });
      
      toast.success('Communication logged');
      setDialogOpen(false);
      setFormData({
        customer_id: '',
        lead_id: '',
        contact_name: '',
        contact_type: 'call',
        direction: 'outbound',
        subject: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to log communication');
    }
  };

  const handleContactSelect = (type, id) => {
    if (type === 'customer') {
      const customer = customers.find(c => c.id === id);
      setFormData({
        ...formData,
        customer_id: id,
        lead_id: '',
        contact_name: customer?.contact_name || ''
      });
    } else {
      const lead = leads.find(l => l.id === id);
      setFormData({
        ...formData,
        customer_id: '',
        lead_id: id,
        contact_name: lead?.name || ''
      });
    }
  };

  const getContactIcon = (type) => {
    const icons = {
      'call': Phone,
      'email': Mail,
      'sms': MessageSquare,
      'in-person': User
    };
    return icons[type] || MessageSquare;
  };

  const filteredComms = communications.filter(c =>
    c.contact_name.toLowerCase().includes(search.toLowerCase()) ||
    c.notes.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Communications</h1>
          <p className="text-gray-500 mt-1">Track all customer interactions</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="log-communication-btn">
              <Plus className="w-4 h-4 mr-2" />
              Log Communication
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Communication</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>Link to Customer</Label>
                <Select 
                  value={formData.customer_id || "none"} 
                  onValueChange={(v) => handleContactSelect('customer', v === "none" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.contact_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Or Link to Lead</Label>
                <Select 
                  value={formData.lead_id || "none"} 
                  onValueChange={(v) => handleContactSelect('lead', v === "none" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select lead (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {leads.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="Who did you communicate with?"
                  data-testid="comm-contact-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select 
                    value={formData.contact_type} 
                    onValueChange={(v) => setFormData({...formData, contact_type: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Phone Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS/Text</SelectItem>
                      <SelectItem value="in-person">In Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Direction</Label>
                  <Select 
                    value={formData.direction} 
                    onValueChange={(v) => setFormData({...formData, direction: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outbound">Outbound (We contacted)</SelectItem>
                      <SelectItem value="inbound">Inbound (They contacted)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Subject (Optional)</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Brief summary"
                />
              </div>

              <div>
                <Label>Notes *</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="What was discussed?"
                  className="min-h-[100px]"
                  data-testid="comm-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="comm-submit-btn">
                  Log Communication
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search communications..."
              className="pl-10"
              data-testid="comm-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Communications List */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : filteredComms.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">No communications logged yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredComms.map((comm) => {
            const Icon = getContactIcon(comm.contact_type);
            return (
              <Card key={comm.id} className="border-0 shadow-sm" data-testid={`comm-card-${comm.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      comm.direction === 'inbound' ? 'bg-blue-100' : 'bg-lime-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        comm.direction === 'inbound' ? 'text-blue-600' : 'text-lime-600'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{comm.contact_name}</h3>
                        {comm.direction === 'inbound' ? (
                          <ArrowDownLeft className="w-4 h-4 text-blue-500" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-lime-500" />
                        )}
                        <span className="text-sm text-gray-500 capitalize">{comm.contact_type}</span>
                      </div>
                      
                      {comm.subject && (
                        <p className="text-sm font-medium text-gray-700 mb-2">{comm.subject}</p>
                      )}
                      
                      <p className="text-gray-600">{comm.notes}</p>
                      
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(comm.created_at).toLocaleString()}
                        </span>
                        <span>Logged by {comm.logged_by}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
